/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import { DatePicker, Row, Col, Input, Button, Space, Form } from 'antd';
import BoxIcon from '../../Icons/BoxIcon';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';
import { ArrowRightLongIcon } from '../../Icons/ArrowRightLongIcon';

// Misc imports
import { DEFAULT_DATETIME_FORMAT } from '../../../constants/main';

// Constant declarations
const { RangePicker } = DatePicker;
const PICKER_DISPLAY_FORMAT = 'MMM DD, YYYY - HH:mm';
const PICKER_EDIT_FORMAT = DEFAULT_DATETIME_FORMAT;

/**
 * Form.Item & Input component to be used inside the range picker's panel
 * @param name The name of the input field
 * @param form The form instance
 * @param onChange Callback function that is triggered when the value changes
 * @param isActive Whether the input is active or not
 * @param onFocus Callback function that is triggered when the input is focused
 * @param placeholder The placeholder text
 * @param localValue The local value of the input
 * @returns {JSX.Element}
*/
const PanelInput = ({
  name,
  form,
  onChange,
  isActive,
  onFocus,
  placeholder,
  localValue
}) => {
  return (
    <Form.Item name={name} noStyle>
      <Input
        placeholder={placeholder}
        className={isActive ? 'bg-primary-light ant-input-focused' : ''}
        onMouseDown={(e) => e.stopPropagation()} // Input focus won't work without this
        onFocus={onFocus}
        onChange={(e) => {
          // If the value is a valid date and the format is correct
          const date = moment(e.target.value, PICKER_EDIT_FORMAT, true);
          if (date.isValid()) {
            onChange(e.target.value);
          }
        }}
        onBlur={() => {
          const value = form.getFieldValue(name);
          const date = moment(value, PICKER_EDIT_FORMAT, true);

          // If the date is invalid, reset it to the local value
          if (!date.isValid()) {
            form.setFieldsValue({
              [name]: moment(localValue).format(PICKER_EDIT_FORMAT)
            });
          }
        }}
      />
    </Form.Item>
  );
};

/**
 * Extends DatePicker component. Supports all DatePicker.RangePicker props
 * A range picker returns an array of two values, the first value is the start date and the second value is the end date
 * Instead of the default AntD RangePicker, it contains two DatePicker components to select the start and end date
 * @param format The date format to display, default is PICKER_DISPLAY_FORMAT
 * @param className Additional class name to apply
 * @param onChange Callback function that is triggered when the value changes
 * @param afterChange Callback function that is triggered after the value changes
 * @param value The value of the range picker
 * @param defaultValue The default value of the range picker
 * @param ranges Preset ranges for quick selection
 * @param rangePickerProps The rest of the supported RangePicker props
 * @returns {JSX.Element}
 */
const CustomRangePicker = ({
  format = PICKER_DISPLAY_FORMAT,
  className = '',
  onChange,
  afterChange,
  value,
  defaultValue,
  ranges,
  timezone,
  ...rangePickerProps
}) => {
  const { t } = useTranslation();
  const [panelForm] = Form.useForm();
  const [localValues, setLocalValues] = useState(value || defaultValue || [moment().startOf('day'), moment().endOf('day')]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activePickerIndex, setActivePickerIndex] = useState(0); // State to control which picker is active

  const panelRef = useRef(null);
  const rangePickerRef = useRef(null);

  const handleOnApply = () => {
    if (onChange) {
      onChange(localValues);
    }

    if (afterChange) {
      afterChange(localValues);
    }

    setIsPanelOpen(false);

    // Unfocus the input
    rangePickerRef.current.blur();
  };

  const handleOnCancel = () => {
    setIsPanelOpen(false);
    setLocalValues(value);

    // Reset the form values
    panelForm.setFieldsValue({
      startDate: value[0].format(PICKER_EDIT_FORMAT),
      endDate: value[1].format(PICKER_EDIT_FORMAT)
    });

    // Unfocus the input
    rangePickerRef.current.blur();
  };

  // Handle clicks outside the panel to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPanelOpen && panelRef.current && !panelRef.current.contains(event.target)) {
        handleOnCancel();
        if (document.activeElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen]);

  const isDateRangeValid = (dates) => {
    return dates
      && dates.length === 2
      && dates[0] && dates[1]
      && moment.isMoment(dates[0]) && dates[0].isValid()
      && moment.isMoment(dates[1]) && dates[1].isValid();
  };

  return (
    <Fragment>
      <RangePicker
        format={format}
        value={localValues}
        ref={rangePickerRef}
        inputReadOnly
        suffixIcon={<BoxIcon name="calendar" className="text-color-default" />}
        separator={<ArrowRightIcon className="text-muted" />}
        open={isPanelOpen}
        className={classNames(className, {
          'ant-picker-focused': isPanelOpen
        })}
        activePickerIndex={activePickerIndex} // Control which date the calendar is picking based on focused input
        onFocus={() => setIsPanelOpen(true)} // Open the panel when the input is focused
        onMouseDown={(e) => {
        // Switch the active picker when the input is clicked for the first time
          const inputs = e.currentTarget.querySelectorAll('input');
          if (e.target === inputs[0]) {
            setActivePickerIndex(0);
          } else if (e.target === inputs[1]) {
            setActivePickerIndex(1);
          }
        }}
        onCalendarChange={(dates) => {
        // If the value is a valid date and the format is correct
          if (isDateRangeValid(dates)) {
          // Add 23:59 to the end date when selected via the calendar
            const updatedEndDate = dates[1].clone().set({ hour: 23, minute: 59 });

            // Update the form values
            panelForm.setFieldsValue({
              startDate: dates[0].format(PICKER_EDIT_FORMAT),
              endDate: updatedEndDate.format(PICKER_EDIT_FORMAT)
            });

            // Update the localValues with the adjusted end date
            setLocalValues([dates[0], updatedEndDate]);
          }

          // Switch to the other picker when the first date is selected
          if (activePickerIndex === 0) {
            setActivePickerIndex(1);
          } else {
            setActivePickerIndex(0);
          }
        }}
        panelRender={(panel) => (
          <Row
            ref={panelRef}
            className="w-100 d-inline-grid"
          >
            <Col span={24}>
              <Row>
                <Col flex="none">
                  {panel}
                </Col>

                {
                  ranges ? (
                    <Col flex="auto" className="border-left border-bottom p-3">
                      <Space direction="vertical" size={10}>
                        {
                          Object.keys(ranges).map((range) => (
                            <Button
                              type="text"
                              block
                              key={range}
                              className="fw-normal"
                              onClick={() => {
                                setLocalValues(ranges[range]);

                                // Update the form values
                                panelForm.setFieldsValue({
                                  startDate: ranges[range][0].format(PICKER_EDIT_FORMAT),
                                  endDate: ranges[range][1].format(PICKER_EDIT_FORMAT)
                                });

                                // Reset activePickerIndex to 0 to focus on the start date
                                setActivePickerIndex(0);
                              }}
                            >
                              {range}
                            </Button>
                          ))
                        }
                      </Space>
                    </Col>
                  ) : null
                }
              </Row>
            </Col>

            <Col span={24} className="p-3">
              <Form
                form={panelForm}
                layout="vertical"
                initialValues={{
                  startDate: localValues[0]?.format(PICKER_EDIT_FORMAT),
                  endDate: localValues[1]?.format(PICKER_EDIT_FORMAT)
                }}
                onFinish={handleOnApply}
              >
                <Row gutter={16}>
                  <Col flex="auto">
                    <Row wrap={false} align="middle">
                      <Col flex="auto">
                        <PanelInput
                          name="startDate"
                          form={panelForm}
                          onChange={(value) => setLocalValues([moment(value, PICKER_EDIT_FORMAT, true), localValues[1]])}
                          isActive={activePickerIndex === 0}
                          onFocus={() => setActivePickerIndex(0)}
                          placeholder={t('Start date')}
                          localValue={localValues[0]}
                        />
                      </Col>

                      <Col flex="none" className="mx-4">
                        <ArrowRightLongIcon />
                      </Col>

                      <Col flex="auto">
                        <PanelInput
                          name="endDate"
                          form={panelForm}
                          onChange={(value) => setLocalValues([localValues[0], moment(value, PICKER_EDIT_FORMAT, true)])}
                          isActive={activePickerIndex === 1}
                          onFocus={() => setActivePickerIndex(1)}
                          placeholder={t('End date')}
                          localValue={localValues[1]}
                        />
                      </Col>
                    </Row>
                  </Col>

                  <Col flex="none">
                    <Space>
                      <Button onClick={handleOnCancel}>
                        {t('Cancel')}
                      </Button>

                      <Button type="primary" htmlType="submit">
                        {t('Apply')}
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        )}
        {...rangePickerProps}
      />

      {
        timezone && (
          <div className="text-muted fs-sm">
            {t('Timezone')}: {timezone}
          </div>
        )
      }
    </Fragment>
  );
};

CustomRangePicker.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  afterChange: PropTypes.func,
  format: PropTypes.string,
  value: PropTypes.array,
  defaultValue: PropTypes.array,
  ranges: PropTypes.object,
  timezone: PropTypes.string
};

PanelInput.propTypes = {
  name: PropTypes.string,
  form: PropTypes.object,
  onChange: PropTypes.func,
  isActive: PropTypes.bool,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  localValue: PropTypes.object
};

export default CustomRangePicker;
